import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { KeyboardEventHandler, useCallback, useState } from "react";
import { match } from "ts-pattern";

import { LoadingTable } from "~/components/database/detail/database-content/LoadingTable";
import { PcDatabaseHeader } from "~/components/database/detail/database-content/database-header/pc";
import {
  ColumnPosition,
  Props,
  SortRule,
  buildTabHeadings,
  generateSortedInfoByOrder,
} from "~/components/database/detail/database-content/lib";
import ResponseTable from "~/components/database/detail/database-content/response-table/ResponseTable";
import { TabHeadingButtonsProps } from "~/components/database/detail/database-content/response-table/TableHeaderButton";
import { VerifiedDbLabel } from "~/extensions/hojokin";
import { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import { DatabaseDetailTableContentQuery } from "~/lib/graphql/client";

const PcDatabaseContent = ({
  form,
  form: { questions },
  responses,
  openResponseForm,
  openItemDetail,
}: Props & { responses: UseQueryResult<DatabaseDetailTableContentQuery> }) => {
  const theme = useTheme();
  const [sortRule, setSortRule] = useState<SortRule>({
    columnPosition: "rowNumber",
    order: "asc",
  });

  const onTableHeadingClickHandler = useCallback(
    (columnPosition: ColumnPosition) => {
      return () => {
        setSortRule((prevState) => {
          return generateSortedInfoByOrder(prevState, columnPosition);
        });
      };
    },
    []
  );

  const onTableHeadingKeyDownHandler = useCallback(
    (columnPosition: ColumnPosition): KeyboardEventHandler => {
      return (event) => {
        if (event.code === "Enter" || event.code === "Space") {
          setSortRule((prevState) => {
            return generateSortedInfoByOrder(prevState, columnPosition);
          });
        }
      };
    },
    []
  );

  const openModal = useCallback(() => {
    openResponseForm(questions);
  }, [openResponseForm, questions]);

  const tabHeadings: TabHeadingButtonsProps[] = buildTabHeadings(
    questions,
    sortRule,
    onTableHeadingClickHandler,
    onTableHeadingKeyDownHandler
  );

  return (
    <div css={rootStyle(theme)}>
      <PcDatabaseHeader openModal={openModal} form={form} />
      <div css={tableWrapperStyle}>
        {match(responses)
          .with({ status: "pending" }, () => <LoadingTable />)
          .with({ status: "error" }, (matched) => (
            <p>{matched.error.message}</p>
          ))
          .with({ status: "success" }, (matched) => (
            <ResponseTable
              form={form}
              tabHeadings={tabHeadings}
              sortRule={sortRule}
              responses={matched.data.form.responses}
              openItemDetail={openItemDetail}
            />
          ))
          .exhaustive()}
      </div>
      <VerifiedDbLabel id={form.id} />
    </div>
  );
};

const rootStyle = (theme: Theme) => css`
  color: ${theme.palette.text.primary};
`;

const shrinkDatabaseHeaderStyle = (theme: Theme) => css`
  position: sticky;
  top: 0;
  background-color: ${theme.palette.background.paper};
  border-bottom: 1px solid ${theme.palette.divider};
`;

const shrinkTableHeaderStyle = css`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
`;

const tableWrapperStyle = css`
  width: 100%;
  overflow-x: auto;
`;

export const tableCellStyle = css`
  padding: 24px;
  flex-grow: 0;
  flex-shrink: 0;
  word-break: break-word;

  // NOTE: iOS の Safari では文字サイズを自動で調整する機能がついているので、勝手に文字サイズ変更が行われないようにする
  // https://developer.mozilla.org/en-US/docs/Web/CSS/text-size-adjust
  text-size-adjust: 100%;

  &:first-of-type {
    padding: 24px 0;
  }
`;

export const smTableCellStyle = css`
  width: 150px;
`;

export const mdTableCellStyle = css`
  width: 300px;
`;

export const lgTableCellStyle = css`
  width: 420px;
`;

export const xlTableCellStyle = css`
  width: 600px;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    width: 98vw;
  }
`;

export default PcDatabaseContent;
